import { isHabitEnded } from '@griply/utils';
import { useHabits } from 'features/tasks';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Section, Sizes } from 'shared/components/ui/section';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { Habit } from 'shared/types/habit';

import { useWeekDate } from '../../contexts/timeframe';
import { HabitsList } from '../habits-list';

export const CompletedHabitsList: React.FC = () => {
  const { t } = useTranslation();
  const today = useToday();
  const weekDate = useWeekDate();
  const completedHabits = useHabits({
    completedOnly: true,
    unArchivedOnly: true,
  });
  const unfinishedHabits = useHabits({
    uncompletedOnly: true,
    unArchivedOnly: true,
  });

  // Habits whose schedules have all expired are done too — they live alongside
  // the completed-forever habits in this section.
  const endedHabits = useMemo(
    () =>
      unfinishedHabits.filter(
        (habit) =>
          !habit.frozenAt &&
          !habit.parentIds?.length &&
          isHabitEnded(habit.schedules, today),
      ),
    [unfinishedHabits, today],
  );

  const habits = useMemo(
    () => [...completedHabits, ...endedHabits],
    [completedHabits, endedHabits],
  );

  const openTaskDetail = useOpenTaskDetail();

  const onHabit = useCallback(
    (habit: Habit) => {
      if (!habit.frozenAt) {
        openTaskDetail(habit);
      }
    },
    [openTaskDetail],
  );

  if (!habits.length) {
    return null;
  }

  return (
    <Section
      title={t('pages.habits.completed-section.title')}
      titleCount={habits.length}
      size={Sizes.Small}
      canCollapse
    >
      <HabitsList items={habits} onHabit={onHabit} weekDate={weekDate} />
    </Section>
  );
};
